import React from 'react';
import { 
  Star, MapPin, Calendar, Package, Phone,
  Shield, CheckCircle, Store, Users, Award, Clock,
  ChevronRight, Camera, ShoppingCart, MessageCircle
} from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import ProductGrid from '../home/ProductGrid';
import ReviewItem from '../../components/listing/ReviewItem';
import { getImageUrl } from '../../utils/imageUtils';

const DesktopSellerDetail = ({
  seller,
  sellerName,
  rating,
  totalReviews,
  recentReviews,
  filteredListings,
  reviews,
  reviewsLoading,
  activeTab,
  setActiveTab,
  listingsFilter,
  setListingsFilter,
  showAllReviews,
  setShowAllReviews,
  isFavorited,
  viewMode,
  setViewMode,
  sortBy,
  setSortBy,
  currentPage,
  setCurrentPage,
  stats,
  handleContactSeller,
  handleViewListing,
  renderStars,
  formatReviewData,
  formatPrice,
  formatDate,
  getImageUrl,
  calculateAverageRating,
  navigate
}) => {
  const renderStatCard = (icon, label, value, valueColor = "text-black") => (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gradient-to-br from-orange-100 to-orange-200 rounded-lg text-[#ff660e]">
          {icon}
        </div>
        <div>
          <p className={`text-xl font-bold ${valueColor}`}>
            {typeof value === "number" ? value.toLocaleString() : value}
          </p>
          <p className="text-xs text-gray-600 mt-0.5">{label}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="hidden lg:block min-h-screen bg-gray-50">
      {/* Desktop Header */}
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Cover Photo Section - Full width within container */}
        <div className="relative mb-6 rounded-2xl overflow-hidden h-[250px]">
          {/* Cover Photo */}
          <div 
            className="w-full h-full bg-cover bg-center relative"
            style={{
                backgroundImage: `url(${getImageUrl(
                  seller?.store_cover_picture_url ||
                  'https://via.placeholder.com/1200x300/f5f5f5/cccccc?text=Store+Cover+Photo'
                )})`,
                backgroundSize: 'cover'
              }}
          >
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/40" />
            
            {/* Store Info Overlay - Positioned at bottom of cover */}
            <div className="absolute bottom-0 left-0 right-0 p-6 pb-5">
              <div className="flex items-end">
                {/* Logo */}
                <div className="relative mr-4">
                  {seller.store_logo_url ? (
                    <img
                      src={getImageUrl(seller.store_logo_url)}
                      alt={sellerName}
                      className="w-24 h-24 rounded-lg border-4 border-white shadow-xl object-cover"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-lg border-4 border-white shadow-xl bg-gradient-to-r from-[#ff660e] to-red-500 flex items-center justify-center text-white font-bold text-3xl">
                      {sellerName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  {seller.is_own_profile && (
                    <div className="absolute -bottom-1 -right-1 bg-orange-600 w-7 h-7 rounded-full flex items-center justify-center border-2 border-white">
                      <Camera className="w-3.5 h-3.5 text-white" />
                    </div>
                  )}
                </div>

                {/* Store Name and Basic Info */}
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-white mb-2 drop-shadow-lg">
                    {sellerName}
                  </h1>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center bg-black/30 rounded-full px-3 py-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="text-sm text-white ml-1 font-medium">{rating.toFixed(1)}</span>
                      <span className="text-xs text-white/70 ml-1">({totalReviews})</span>
                    </div>
                    <div className="flex items-center bg-black/30 rounded-full px-3 py-1">
                      <ShoppingCart className="w-4 h-4 text-white" />
                      <span className="text-sm text-white ml-1">{filteredListings.length} items</span>
                    </div>
                    {seller.store_location && (
                      <div className="flex items-center bg-black/30 rounded-full px-3 py-1">
                        <MapPin className="w-4 h-4 text-white" />
                        <span className="text-sm text-white ml-1 truncate max-w-[150px]">
                          {seller.store_location}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact Button */}
                <button 
                  onClick={handleContactSeller}
                  className="bg-white text-[#ff660e] px-6 py-3 rounded-xl font-semibold hover:bg-orange-50 transition-colors flex items-center gap-2 shadow-lg"
                >
                  <MessageCircle className="w-5 h-5" />
                  Contact Seller
                </button>
              </div>
            </div>

            {/* Verification Badges - Top right */}
            <div className="absolute top-4 right-4 flex gap-2">
              {seller.is_email_verified && (
                <div className="bg-green-500 text-white px-3 py-1.5 rounded-full flex items-center gap-1 shadow-lg" title="Email Verified">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-xs font-medium">Email Verified</span>
                </div>
              )}
              {seller.is_phone_verified && (
                <div className="bg-blue-500 text-white px-3 py-1.5 rounded-full flex items-center gap-1 shadow-lg" title="Phone Verified">
                  <Shield className="w-4 h-4" />
                  <span className="text-xs font-medium">Phone Verified</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* About and Delivery Info Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* About This Store */}
          <div className="md:col-span-2 bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-black mb-3">About This Store</h3>
            <p className="text-gray-700 leading-relaxed">
              {seller.store_description || 'No description provided.'}
            </p>
            
            {/* Meta Info */}
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center text-gray-600">
                <Calendar className="w-4 h-4 mr-2" />
                <span className="text-sm">Seller since {formatDate(seller.created_at || seller.user_created_at)}</span>
              </div>
              {seller.phone_number && (
                <div className="flex items-center text-gray-600">
                  <Phone className="w-4 h-4 mr-2" />
                  <span className="text-sm">{seller.phone_number}</span>
                </div>
              )}
            </div>
          </div>

          {/* Delivery Information */}
          {seller.delivery_options && (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-black mb-4">Delivery Information</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-1">Delivery Options</h4>
                  <p className="text-black capitalize">{seller.delivery_options}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-1">Delivery Fee</h4>
                  <p className="text-black">
                    {seller.delivery_fee && parseFloat(seller.delivery_fee) > 0 
                      ? `₦${parseFloat(seller.delivery_fee).toLocaleString()}` 
                      : 'Free delivery'}
                  </p>
                </div>
                {seller.delivery_coverage_areas && Array.isArray(seller.delivery_coverage_areas) && seller.delivery_coverage_areas.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-600 mb-2">Coverage Areas</h4>
                    <div className="flex flex-wrap gap-2">
                      {seller.delivery_coverage_areas.map((area, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800"
                        >
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {renderStatCard(
            <Package className="w-6 h-6" />,
            'Total Listings',
            filteredListings.length
          )}
          {renderStatCard(
            <Users className="w-6 h-6" />,
            'Total Reviews',
            totalReviews
          )}
          {renderStatCard(
            <Award className="w-6 h-6" />,
            'Average Rating',
            rating.toFixed(1)
          )}
          {renderStatCard(
            <Clock className="w-6 h-6" />,
            'Response Rate',
            stats?.response_rate ? `${stats.response_rate}%` : 'N/A'
          )}
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 bg-gray-100 p-1 rounded-xl mb-6">
          <button
            onClick={() => setActiveTab('listings')}
            className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'listings'
                ? 'bg-white shadow text-[#ff660e]'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Store className="w-5 h-5" />
            <span className="font-medium">Listings ({filteredListings.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'reviews'
                ? 'bg-white shadow text-[#ff660e]'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Star className="w-5 h-5" />
            <span className="font-medium">Reviews ({totalReviews})</span>
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'listings' ? (
          <div>
            {/* Listings Filter */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <select 
                  value={listingsFilter}
                  onChange={(e) => setListingsFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#ff660e] text-black font-medium"
                >
                  <option value="active">Active Listings</option>
                  <option value="featured">Featured</option>
                  <option value="recent">Most Recent</option>
                </select>
              </div>
              <span className="text-sm text-gray-600">{filteredListings.length} items</span>
            </div>

            {/* ProductGrid Component */}
            <ProductGrid
              listings={filteredListings}
              viewMode={viewMode}
              sortBy={sortBy}
              currentPage={currentPage}
              onViewModeChange={setViewMode}
              onSortChange={setSortBy}
              onPageChange={setCurrentPage}
              showControls={true}
              isSearchPage={false}
              hidePagination={false}
            />
          </div>
        ) : (
          /* Reviews Tab - Keep as is */
          <div className="bg-white rounded-2xl shadow-lg p-6">
            {reviewsLoading ? (
              <div className="flex justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#ff660e] mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading reviews...</p>
                </div>
              </div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-12">
                <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-black mb-2">No reviews yet</h3>
                <p className="text-gray-600 mb-4">This seller hasn't received any reviews yet.</p>
                <p className="text-sm text-gray-500">Be the first to review this seller after a transaction!</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Rating Summary */}
                <div className="flex items-center gap-6 mb-6">
                  <div className="flex items-center">
                    <Star className="w-8 h-8 fill-current text-yellow-400" />
                    <span className="text-3xl font-bold ml-3">{calculateAverageRating()}</span>
                    <span className="text-gray-500 ml-1">/5</span>
                  </div>
                  <div>
                    <p className="text-gray-700">
                      Based on {reviews.length} review{reviews.length !== 1 ? 's' : ''}
                    </p>
                    {recentReviews > 0 && (
                      <p className="text-sm text-gray-500 mt-1">
                        {recentReviews} review{recentReviews !== 1 ? 's' : ''} in the last 30 days
                      </p>
                    )}
                  </div>
                </div>

                {/* Distribution Stats */}
                {stats?.distribution && stats.distribution.length > 0 && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-black mb-3">Rating Distribution</h4>
                    <div className="space-y-2">
                      {[5, 4, 3, 2, 1].map((star) => {
                        const distribution = stats.distribution.find(d => d.rating_overall === star);
                        const count = distribution?.count || 0;
                        const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                        
                        return (
                          <div key={star} className="flex items-center">
                            <div className="w-12 flex items-center">
                              <Star className="w-4 h-4 fill-current text-yellow-400" />
                              <span className="ml-1 text-sm">{star}</span>
                            </div>
                            <div className="flex-1 ml-4">
                              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-yellow-400 rounded-full"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                            <div className="w-12 text-right text-sm text-gray-600">
                              {count}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Reviews List */}
                <div className="space-y-6">
                  {(showAllReviews ? reviews : reviews.slice(0, 5)).map((review) => (
                    <div key={review.rating_id} className="border-b border-gray-200 pb-6 last:border-0">
                      <ReviewItem review={formatReviewData(review)} />
                      
                      {review.listing_title && (
                        <div className="mt-4 flex items-center justify-between text-sm">
                          <div className="flex items-center text-gray-600">
                            <Package className="w-4 h-4 mr-2" />
                            <span>For: {review.listing_title}</span>
                            {review.listing_price && (
                              <span className="ml-2 text-[#ff660e] font-medium">
                                {formatPrice(review.listing_price)}
                              </span>
                            )}
                          </div>
                          <button 
                            onClick={() => navigate(`/listings/${review.listing_id}`)}
                            className="text-[#ff660e] hover:text-[#ff5500] font-medium"
                          >
                            View listing
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {reviews.length > 5 && (
                    <div className="pt-4">
                      <button
                        onClick={() => setShowAllReviews(!showAllReviews)}
                        className="w-full border-2 border-[#ff660e] text-[#ff660e] py-3 rounded-xl font-semibold hover:bg-orange-50 transition-colors flex items-center justify-center gap-2"
                      >
                        {showAllReviews ? 'Show Less' : `Show ${reviews.length - 5} More Reviews`}
                        <ChevronRight className={`w-5 h-5 transition-transform ${showAllReviews ? 'rotate-90' : ''}`} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
      
      {/* Desktop Footer */}
      <Footer />
    </div>
  );
};

export default DesktopSellerDetail;